/* ============================================================
   delivery.js: free delivery progress bars and the dispatch message.
   Display only. What checkout charges is set in Settings > Shipping and
   delivery and the SMART Shipping Rates & Rules app; see the comment at
   the top of snippets/free-delivery.liquid.

   - Bars ([data-fd-bar], snippets/free-delivery.liquid) and the heavy
     order notice ([data-fd-heavy]) are painted by Liquid, then updated here
     from /cart.js after every cart change: cart.js calls
     FAWPDelivery.update(cart) when it already has the cart, and a
     PerformanceObserver catches any other /cart/add, /cart/change,
     /cart/update or /cart/clear request (apps, the pump finder) and
     refetches /cart.js.
   - The dispatch message ([data-fd-dispatch], snippets/dispatch-message)
     is worked out in UK time (Europe/London), whatever the visitor's own
     clock or time zone, skipping weekends and the bank holidays below.
   ============================================================ */
(function () {
  'use strict';

  var cfg = (window.theme && window.theme.delivery) || {};

  /* England & Wales bank holidays: no dispatch on these days.
     Source: https://www.gov.uk/bank-holidays.json ("england-and-wales").
     UPDATE THIS LIST once a year: add the next year's dates before the
     end of 2027. After the last date here, bank holidays are treated as
     ordinary working days. */
  var BANK_HOLIDAYS = [
    '2026-01-01', '2026-04-03', '2026-04-06', '2026-05-04', '2026-05-25', '2026-08-31', '2026-12-25', '2026-12-28',
    '2027-01-01', '2027-03-26', '2027-03-29', '2027-05-03', '2027-05-31', '2027-08-30', '2027-12-27', '2027-12-28'
  ];
  var DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  /* ---------- Dispatch message ---------- */

  /* The current date and time in the UK, from the visitor's clock. */
  function ukNow() {
    var parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
    }).formatToParts(new Date());
    var o = {};
    parts.forEach(function (p) { o[p.type] = p.value; });
    return { y: +o.year, m: +o.month, d: +o.day, h: (+o.hour) % 24, min: +o.minute };
  }

  /* A calendar day `offset` days after y-m-d (pure date arithmetic in UTC,
     so clock changes can't shift it). */
  function calendarDay(y, m, d, offset) {
    var t = new Date(Date.UTC(y, m - 1, d + offset));
    return { iso: t.toISOString().slice(0, 10), dow: t.getUTCDay(), date: t.getUTCDate(), month: t.getUTCMonth() };
  }

  function isWorkingDay(day) {
    return day.dow >= 1 && day.dow <= 5 && BANK_HOLIDAYS.indexOf(day.iso) === -1;
  }

  function dispatchHtml() {
    var cutoff = parseInt(cfg.cutoffHour, 10);
    if (isNaN(cutoff)) cutoff = 14;
    var now = ukNow();
    var today = calendarDay(now.y, now.m, now.d, 0);
    var nowMin = now.h * 60 + now.min;

    /* Working day, before the cutoff: count down to it. */
    if (isWorkingDay(today) && nowMin < cutoff * 60) {
      var left = cutoff * 60 - nowMin;
      var h = Math.floor(left / 60);
      var m = left % 60;
      return 'Order within <strong>' + (h > 0 ? h + 'h ' : '') + m + 'm</strong> for dispatch today.';
    }

    /* Otherwise name the next working day. */
    for (var k = 1; k <= 14; k++) {
      var next = calendarDay(now.y, now.m, now.d, k);
      if (!isWorkingDay(next)) continue;
      if (k === 1 && next.dow !== 1) return 'Order now for dispatch tomorrow (next working day).';
      if (k <= 6) return 'Order now for dispatch on ' + DAY_NAMES[next.dow] + '.';
      return 'Order now for dispatch on ' + DAY_NAMES[next.dow] + ' ' + next.date + ' ' + MONTH_NAMES[next.month] + '.';
    }
    return '';
  }

  /* A basket's dispatch message only shows when every line can go out
     today (no lead time, in stock): lines carry data-dispatch-ok, set in
     Liquid from snippets/line-dispatch-note. */
  function cartDispatchOk(el) {
    var scope = el.closest('[data-fd-cart]');
    if (!scope) return true;
    /* A quoted (heavy) basket can't check out, so no countdown. */
    var heavy = scope.querySelector('[data-fd-heavy]');
    if (heavy && !heavy.hidden) return false;
    var lines = scope.querySelectorAll('[data-cart-line]');
    return lines.length > 0 && !scope.querySelector('[data-cart-line][data-dispatch-ok="0"]');
  }

  /* Never throws: cart.js calls this inside its add-to-basket chain, and a
     failure there would resubmit the form. The Liquid text stays on error. */
  function updateDispatch() {
    try {
      var html = dispatchHtml();
      $$('[data-fd-dispatch]').forEach(function (el) {
        var text = el.querySelector('[data-fd-dispatch-text]');
        if (text && html) text.innerHTML = html;
        if (el.hasAttribute('data-fd-dispatch-scope')) el.hidden = !cartDispatchOk(el);
      });
    } catch (e) { /* keep the fallback wording */ }
  }

  /* ---------- Free delivery bars ---------- */

  /* Shop money format, without pence when the amount is whole pounds
     (the same as Liquid's money_without_trailing_zeros). */
  function formatMoney(cents) {
    var format = (window.theme && window.theme.moneyFormat) || '£{{amount}}';
    var amount = (Math.max(0, cents) / 100).toFixed(2).replace(/\.00$/, '');
    var parts = amount.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format.replace(/\{\{\s*\w+\s*\}\}/, parts.join('.'));
  }

  var lastUpdate = 0;

  function update(cart) {
    try { render(cart); } catch (e) { /* display only: never break the caller */ }
  }

  function render(cart) {
    if (!cart || typeof cart.items_subtotal_price === 'undefined') return;
    lastUpdate = Date.now();
    var subtotal = cart.items_subtotal_price;
    var weight = cart.total_weight || 0;

    $$('[data-fd-bar]').forEach(function (bar) {
      var threshold = parseInt(bar.getAttribute('data-fd-threshold'), 10) || 0;
      var maxGrams = parseInt(bar.getAttribute('data-fd-max-grams'), 10) || 0;
      var heavyGrams = parseInt(bar.getAttribute('data-fd-heavy-grams'), 10) || 0;
      /* Same order as snippets/free-delivery.liquid: over the weight limit
         can't qualify, whatever the value. 0 means no weight limit. */
      var state;
      if (cart.item_count === 0) state = 'empty';
      else if (maxGrams > 0 && weight > maxGrams) state = 'overweight';
      else if (subtotal < threshold) state = 'below';
      else state = 'qualified';

      bar.hidden = heavyGrams > 0 && weight > heavyGrams;
      bar.setAttribute('data-fd-state', state);
      $$('[data-fd-msg]', bar).forEach(function (m) { m.hidden = m.getAttribute('data-fd-msg') !== state; });
      $$('[data-fd-msg-extra]', bar).forEach(function (m) { m.hidden = m.getAttribute('data-fd-msg-extra') !== state; });
      var remaining = bar.querySelector('[data-fd-remaining]');
      if (remaining) remaining.textContent = formatMoney(threshold - subtotal);

      var pct = 0;
      if (state === 'below' && threshold > 0) pct = Math.floor(subtotal * 100 / threshold);
      else if (state !== 'empty') pct = 100;
      var track = bar.querySelector('[role="progressbar"]');
      if (track) {
        track.hidden = state === 'empty';
        track.setAttribute('aria-valuenow', pct);
        var fill = track.firstElementChild;
        if (fill) fill.style.width = pct + '%';
      }
    });

    $$('[data-fd-heavy]').forEach(function (note) {
      var grams = parseInt(note.getAttribute('data-fd-heavy-grams'), 10) || 0;
      note.hidden = !(grams > 0 && weight > grams);
    });

    /* Drawer "Delivery from £X + VAT": the price for the basket weight, from
       the bands in snippets/delivery-rates.liquid (data-fd-rates). */
    $$('[data-fd-basket-price]').forEach(function (el) {
      var r;
      try { r = JSON.parse(el.getAttribute('data-fd-rates')); } catch (e) { return; }
      if (!r || !r.bands || !r.bands.length) return;
      var pence = r.bands[r.bands.length - 1][1];
      if (weight <= 0) pence = r.bands[0][1];
      else {
        for (var i = 0; i < r.bands.length; i++) {
          if (weight <= r.bands[i][0]) { pence = r.bands[i][1]; break; }
        }
      }
      var fm = window.theme && window.theme.formatMoney;
      el.textContent = fm ? fm(pence).replace(/[.,]00(?!\d)/, '') : '£' + (pence / 100);
    });

    /* Drawer delivery sentence: quoted, free, or the standard wording. */
    $$('[data-fd-cart]').forEach(function (scope) {
      var notes = scope.querySelectorAll('[data-fd-note]');
      if (!notes.length) return;
      var heavy = scope.querySelector('[data-fd-heavy]');
      var bar = scope.querySelector('[data-fd-bar]');
      var noteState = 'other';
      if (heavy && !heavy.hidden) noteState = 'heavy';
      else if (bar && !bar.hidden && bar.getAttribute('data-fd-state') === 'qualified') noteState = 'qualified';
      Array.prototype.forEach.call(notes, function (n) { n.hidden = n.getAttribute('data-fd-note') !== noteState; });
    });

    /* Product page: hide "UK delivery from £X + VAT" while the basket
       qualifies for free delivery (the bar says so right above it). */
    var pdpBar = document.querySelector('.bb-fd [data-fd-bar]');
    $$('[data-fd-std-delivery]').forEach(function (li) {
      li.hidden = !!pdpBar && !pdpBar.hidden && pdpBar.getAttribute('data-fd-state') === 'qualified';
    });

    updateDispatch();
  }

  /* Refetch the cart (debounced) after a cart change we weren't handed. */
  var refreshTimer = null;
  function refresh() {
    if (!document.querySelector('[data-fd-bar], [data-fd-heavy], [data-fd-dispatch-scope]')) return;
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      /* cart.js has just handed us this change: no need to fetch it again. */
      if (Date.now() - lastUpdate < 1000) return;
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(root + 'cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(update)
        .catch(function () { /* keep the last state */ });
    }, 150);
  }

  function watchCartRequests() {
    if (!('PerformanceObserver' in window)) return;
    try {
      new PerformanceObserver(function (list) {
        list.getEntries().forEach(function (entry) {
          if (/\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(entry.name)) refresh();
        });
      }).observe({ type: 'resource' });
    } catch (e) { /* older browsers: cart.js still calls update() directly */ }
  }

  function init() {
    updateDispatch();
    setInterval(updateDispatch, 30000);
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') updateDispatch();
    });
    watchCartRequests();
    /* Back/forward cache: the basket may have changed in another tab. */
    window.addEventListener('pageshow', function (e) { if (e.persisted) refresh(); });
  }

  window.FAWPDelivery = { update: update, refresh: refresh, dispatchHtml: dispatchHtml };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
